import React from 'react'
import NextImage from 'next/image'
import type {
  Image as SanityImage,
  LocaleImage,
  Maybe,
  SanityImageAsset,
} from '@/.generated/sanityTypes/graphql'
import type { Locale } from '@/i18n'
import { useLocale } from 'next-intl'
import { getImageObjectPosition } from '@/lib/image'
import { cx } from '@monorepo/helper/cx'

const isImageAsset = (
  image: SanityImage | SanityImageAsset,
): image is SanityImageAsset => {
  return (image as SanityImage).asset === undefined
}

type SvgOrImgProps = {
  image: SanityImage | SanityImageAsset
  alt?: string
  style?: React.CSSProperties
  className?: string
  fill?: boolean
  priority?: boolean
  sizes?: string
  width?: number
  height?: number
  cover?: boolean
}

const Image = ({
  image,
  alt,
  style,
  className,
  fill,
  priority,
  sizes,
  width = fill
    ? undefined
    : isImageAsset(image)
      ? image?.metadata?.dimensions?.width || undefined
      : image?.asset?.metadata?.dimensions?.width || undefined,
  height = fill
    ? undefined
    : isImageAsset(image)
      ? image?.metadata?.dimensions?.width || undefined
      : image?.asset?.metadata?.dimensions?.height || undefined,
  cover,
}: SvgOrImgProps) => {
  const img = isImageAsset(image) ? image : image?.asset

  if (!img?.url) return null

  if (cover && 'hotspot' in img) {
    style = {
      objectPosition: getImageObjectPosition(img as SanityImage),
      ...style,
    }
  }

  return img.extension === 'svg' ? (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={img.url}
      style={{
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        ...style,
      }}
      alt={alt || img.altText || img.title || ''}
    />
  ) : (
    <NextImage
      src={img.url}
      height={height}
      width={width}
      style={style}
      sizes={sizes}
      className={cx(cover && 'object-cover', className)}
      alt={alt || img.altText || img.title || ''}
      fill={fill}
      priority={priority}
      blurDataURL={img.metadata?.lqip || undefined}
      placeholder="blur"
    />
  )
}

export const LocalizedImage = ({
  localeImage,
  ...props
}: Omit<SvgOrImgProps, 'image'> & {
  localeImage?: Maybe<LocaleImage>
}) => {
  const locale = useLocale() as Locale
  const img = localeImage?.[locale] ?? localeImage?.de

  if (img && img?.asset) {
    // eslint-disable-next-line jsx-a11y/alt-text
    return <Image image={img} {...props} />
  }
}

export default Image
